import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { API } from "./shared";

const inp = { border: "1px solid #CBD5E1", borderRadius: 8, padding: "6px 8px", fontSize: 12.5, width: "100%" };
const KEYS = ["price", "turnaround_days", "shipping_mode", "delivery_charge_inr", "transit_days", "production_note"];
const NUM = ["price", "turnaround_days", "delivery_charge_inr", "transit_days"];

export function AdminBidEditor({ bid, job, onSaved }) {
  const init = () => Object.fromEntries(KEYS.map(k => [k, bid[k] ?? (k === "shipping_mode" ? "shiprocket" : "")]));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const own = form.shipping_mode === "own_courier";
  const save = async () => {
    const body = { reason: reason.trim() };
    KEYS.forEach(k => {
      if (!own && ["delivery_charge_inr", "transit_days"].includes(k)) return;
      const v = NUM.includes(k) ? (form[k] === "" ? null : Number(form[k])) : form[k];
      if (v !== (bid[k] ?? (k === "shipping_mode" ? "shiprocket" : NUM.includes(k) ? null : ""))) body[k] = v;
    });
    if (Object.keys(body).length === 1) { toast.error("No changes to save"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/jobs/${job.id}/bids/${bid.id}`, body);
      toast.success("Bid updated · supplier notified");
      setOpen(false); setReason("");
      await onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Could not update bid");
    } finally { setBusy(false); }
  };
  if (!open) return (
    <button data-testid={`admin-edit-bid-btn-${bid.id}`} className="so-btn" style={{ marginTop: 6, fontSize: 11.5, padding: "4px 10px" }} onClick={() => { setForm(init()); setOpen(true); }}>
      <Pencil size={12}/> Edit bid for supplier
    </button>
  );
  return (
    <div data-testid={`admin-edit-bid-form-${bid.id}`} style={{ marginTop: 8, padding: 10, border: "1px solid #DDD6FE", background: "#F5F3FF", borderRadius: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <label style={{ fontSize: 11.5 }}>Price (₹)<input data-testid="admin-bid-price" type="number" min="1" style={inp} value={form.price} onChange={set("price")}/></label>
      <label style={{ fontSize: 11.5 }}>Turnaround (days)<input data-testid="admin-bid-turnaround" type="number" min="1" max="90" style={inp} value={form.turnaround_days} onChange={set("turnaround_days")}/></label>
      <label style={{ fontSize: 11.5 }}>Shipping
        <select data-testid="admin-bid-shipping" style={inp} value={form.shipping_mode} onChange={set("shipping_mode")}>
          <option value="shiprocket">GoPrintPack logistics</option><option value="own_courier">Supplier's own courier</option>
        </select>
      </label>
      {own ? <>
        <label style={{ fontSize: 11.5 }}>Delivery charge (₹)<input data-testid="admin-bid-delivery" type="number" min="0" style={inp} value={form.delivery_charge_inr ?? ""} onChange={set("delivery_charge_inr")}/></label>
        <label style={{ fontSize: 11.5 }}>Transit days<input data-testid="admin-bid-transit" type="number" min="1" max="30" style={inp} value={form.transit_days ?? ""} onChange={set("transit_days")}/></label>
      </> : <span/>}
      <label style={{ fontSize: 11.5, gridColumn: "1 / -1" }}>Production note<textarea data-testid="admin-bid-note" rows={2} style={inp} value={form.production_note} onChange={set("production_note")}/></label>
      <label style={{ fontSize: 11.5, gridColumn: "1 / -1" }}>Reason (shown to the supplier)<input data-testid="admin-bid-reason" style={inp} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Supplier asked by phone to correct the price"/></label>
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button data-testid="admin-bid-save" className="so-btn so-blue" disabled={busy || reason.trim().length < 5} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
        <button data-testid="admin-bid-cancel" className="so-btn" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}
